import { getApiBaseUrl } from "@/utils/apiUrlUtils";

const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

/**
 * Проверяет, является ли файл изображением
 */
export function isImage(attachment) {
  const type = attachmentType(attachment);
  return (
    type.startsWith("image/") ||
    (attachmentUrl(attachment) &&
      /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(attachmentUrl(attachment)))
  );
}

/**
 * Проверяет, является ли вложение аудиофайлом
 */
export function isAudio(attachment) {
  const type = attachmentType(attachment);
  return (
    type.startsWith("audio/") ||
    (attachmentUrl(attachment) &&
      /\.(ogg|oga|mp3|m4a|wav|webm)$/i.test(attachmentUrl(attachment)))
  );
}

/** Определяет Markdown даже при application/octet-stream от хранилища. */
export function isMarkdown(attachment) {
  return getFileExtension(attachment) === "MD" || /^(text|application)\/(x-)?markdown(?:;|$)/i.test(attachmentType(attachment)) || /\.md(?:[?#]|$)/i.test(attachmentUrl(attachment));
}

/**
 * Возвращает класс иконки по типу файла
 */
export function getFileIconClass(attachment) {
  const type = attachmentType(attachment);
  if (isImage(attachment)) return "image-icon";
  if (isAudio(attachment)) return "audio-icon";
  if (type.includes("pdf")) return "pdf-icon";
  if (type.includes("spreadsheet") || type.includes("excel"))
    return "excel-icon";
  if (type.includes("word")) return "word-icon";
  if (
    type.includes("zip") ||
    type.includes("rar") ||
    type.includes("7z")
  )
    return "archive-icon";
  if (type.includes("text") || type.includes("csv"))
    return "text-icon";
  return "default-icon";
}

/**
 * Извлекает расширение файла
 */
export function getFileExtension(attachment) {
  const name = attachmentName(attachment);
  const dotIndex = name.lastIndexOf(".");
  return dotIndex !== -1
    ? name.substring(dotIndex + 1).toUpperCase()
    : "FILE";
}

/**
 * Возвращает читаемое название типа файла
 */
export function getFileTypeName(attachment) {
  const type = attachmentType(attachment);
  if (isImage(attachment)) return "Изображение";
  if (isAudio(attachment)) return "Аудио";
  if (type.includes("pdf")) return "PDF документ";
  if (type.includes("spreadsheet") || type.includes("excel"))
    return "Excel файл";
  if (type.includes("word")) return "Word документ";
  if (
    type.includes("zip") ||
    type.includes("rar") ||
    type.includes("7z")
  )
    return "Архив";
  if (type.includes("text") || type.includes("csv"))
    return "Текстовый файл";
  if (type.includes("video")) return "Видео файл";
  if (type.includes("audio")) return "Аудио файл";
  return "Файл";
}

/**
 * Обрезает имя файла до указанной длины
 */
export function truncateFileName(name, length = 15) {
  if (!name) return "";
  return name.length <= length
    ? name
    : name.substring(0, length) + "...";
}

/**
 * Форматирует размер файла в читаемый вид
 */
export function formatFileSize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

/**
 * Возвращает имя вложения из плоского API-поля или metadata Shrine
 */
export function attachmentName(attachment = {}) {
  return attachment.name || attachment.file?.metadata?.filename || "";
}

/**
 * Возвращает размер вложения из плоского API-поля или metadata Shrine
 */
export function attachmentSize(attachment = {}) {
  return attachment.size || attachment.file?.metadata?.size || 0;
}

/**
 * Возвращает MIME-тип вложения из плоского API-поля или metadata Shrine
 */
export function attachmentType(attachment = {}) {
  return attachment.type || attachment.file?.metadata?.mime_type || "";
}

/**
 * Возвращает ссылку вложения из нового или legacy API-поля
 */
export function attachmentUrl(attachment = {}) {
  return attachment.url || attachment.file_url || "";
}

/**
 * Возвращает URL полного файла
 */
export function attachmentFileUrl(attachment = {}) {
  const fileUrl = attachmentUrl(attachment);

  return fileUrl ? resolveUrl(fileUrl) : "";
}

/**
 * Возвращает URL для маленького превью с fallback на полный файл
 */
export function attachmentPreviewUrl(attachment = {}) {
  const previewUrl =
    attachment.thumbnail_url ||
    attachmentUrl(attachment);

  if (!previewUrl) return "";

  if (previewUrl.startsWith("data:") || previewUrl.startsWith("blob:")) {
    return previewUrl;
  }

  return resolveUrl(previewUrl);
}

/**
 * Возвращает URL для открытия вложения в полном просмотре
 */
export function attachmentOpenUrl(attachment = {}) {
  const localPreview = attachment.preview || "";

  if (
    localPreview.startsWith("data:") ||
    localPreview.startsWith("blob:")
  ) {
    return localPreview;
  }

  return attachmentFileUrl(attachment);
}

/**
 * Проверяет, превышает ли файл максимальный размер
 */
export function isFileTooLarge(file) {
  return file.size > MAX_FILE_SIZE;
}

/**
 * Создаёт превью изображения (Data URL)
 */
export function createPreview(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.readAsDataURL(file);
  });
}

/**
 * Превращает относительный backend path в абсолютный URL API
 */
export function resolveUrl(fileUrl) {
  if (!fileUrl) return "";
  if (fileUrl.startsWith("http")) return fileUrl;
  return `${getApiBaseUrl()}${fileUrl}`;
}

/**
 * Универсальный экспорт в Excel
 * @param {Object} response - Ответ от API
 * @param {string} defaultFileName - Имя файла по умолчанию
 */
export async function exportToExcel(
  response,
  defaultFileName = "data.xlsx"
) {
  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const contentDisposition = response.headers["content-disposition"];
  const fileName =
    contentDisposition?.match(/filename="(.+?)"/)?.[1] ||
    defaultFileName;

  const url = window.URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), {
    href: url,
    download: fileName,
  });

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export { MAX_FILE_SIZE };
